import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { DARK_LAYER1 } from '../../styles/elevation';
import { cn } from '../../utils/cn';
import type { SurfaceEmptyStateProps } from './SurfaceEmptyState.types';

/**
 * The "this surface has nothing in it yet" card — ONE treatment for every deck
 * surface, on both platforms.
 *
 * Anatomy (artwork · heading · supporting line · CTA, on a tinted rounded card):
 * this is the shape web's Quizzes, Flashcards and Mindmap panes had each
 * arrived at separately, with the numbers drifting — 36 vs 40 radius,
 * `secondary-200` vs `secondary-100` border, `bg-secondary-50` vs `/50`, gap 3
 * vs 6 — while the phone showed a bare centred icon and no card at all. That
 * drift is what the designer read as "recreate the empty state, same as on the
 * web" and "that now is pretty ugly": not one bad screen, but four screens that
 * had stopped being the same component.
 *
 * The artwork is a SLOT (see `SurfaceEmptyStateProps.artwork`) — the only part
 * that is legitimately per-surface and per-platform. Everything else is fixed
 * here so a fifth surface cannot invent a sixth spacing.
 *
 * Labels arrive pre-translated (the DS carries no i18n).
 */
export function SurfaceEmptyState({
  artwork,
  title,
  description,
  action,
  className,
  testID,
}: SurfaceEmptyStateProps) {
  return (
    <View
      className={cn(
        'h-full w-full flex-1 flex-col items-center justify-center gap-6 rounded-[40px]',
        'border border-secondary-100 bg-secondary-50/50 px-6 py-8',
        // Dark: nested in the pane that holds it, never darker (elevation.ts).
        DARK_LAYER1,
        className,
      )}
      testID={testID}>
      {artwork}
      {/* `text-center` sits on each Text, not the wrapper: native has no text
          inheritance through a View, so a wrapper-only rule would centre on web
          and left-align on mobile.

          Both lines carry an EXPLICIT ramp class for that same reason. The
          title used to carry none at all — on web it inherited the document's
          16px and read correctly, while native fell back to RN's fixed 14px
          default, which the iPad rem ramp does not touch. The description's
          `text-b2` DID scale (14 → ~17.5 on a 13" iPad), so the heading ended
          up SMALLER than its own supporting line. `text-h4` is 16/24 — exactly
          what web already rendered, so this is a native-only correction. */}
      <View className="flex-col items-center gap-1">
        <Text className="text-center text-h4 font-semibold text-subtitle-800 native:font-mona-semibold">
          {title}
        </Text>
        {description ? (
          <Text className="max-w-xs text-center text-b2 font-medium text-body-600 native:font-mona-medium">
            {description}
          </Text>
        ) : null}
      </View>
      {/* Capped to the copy column so a CTA never spans a whole expanded board. */}
      {action ? <View className="w-full max-w-xs">{action}</View> : null}
    </View>
  );
}
(SurfaceEmptyState as { displayName?: string }).displayName = 'SurfaceEmptyState';
